// The data room's client side (2026-09-16): the session a firm's password earned, kept for this tab;
// every call to the api's public /dr/* routes carries it as a bearer; the viewer's beacons carry it in
// the body. Nothing here uses HQ's own sign-in.
import { API_URL } from "./api";

export interface RoomSession { token: string; firm: string; exp: number; name: string | null }
const KEY = "shapely.dataroom";

export function getSession(): RoomSession | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as RoomSession;
    if (!s.token || s.exp * 1000 < Date.now()) { window.sessionStorage.removeItem(KEY); return null; }
    return s;
  } catch { return null; }
}
export function setSession(s: RoomSession) { try { window.sessionStorage.setItem(KEY, JSON.stringify(s)); } catch { /* no storage: the gate again next time */ } }
export function clearSession() { try { window.sessionStorage.removeItem(KEY); } catch { /* nothing to clear */ } }

export class RoomError extends Error { constructor(public status: number, message: string) { super(message); } }

/** a call with the session; 401 clears it (a reset or a revoke from HQ, or the twelve hours ran out) */
export async function drFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const s = getSession();
  if (!s) throw new RoomError(401, "sign in again");
  const r = await fetch(`${API_URL}${path}`, { ...init, headers: { ...(init.headers ?? {}), Authorization: `Bearer ${s.token}` } });
  if (r.status === 401) { clearSession(); throw new RoomError(401, "sign in again"); }
  return r;
}

export async function enter(password: string, name: string, email: string): Promise<RoomSession> {
  const r = await fetch(`${API_URL}/dr/enter`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password, name: name || undefined, email: email || undefined }) });
  const j = (await r.json().catch(() => ({}))) as { token?: string; firm?: string; exp?: number; error?: string };
  if (!r.ok || !j.token || !j.firm || !j.exp) throw new RoomError(r.status, j.error ?? "that did not work");
  const s: RoomSession = { token: j.token, firm: j.firm, exp: j.exp, name: name || null };
  setSession(s);
  return s;
}

/** opened a file, or reached a page for so long — fire-and-forget, survives leaving the page */
export function beacon(fileId: string, kind: "open" | "page", page = 1, seconds = 0) {
  const s = getSession();
  if (!s) return;
  const body = JSON.stringify({ t: s.token, fileId, kind, page, seconds });
  if (navigator.sendBeacon) navigator.sendBeacon(`${API_URL}/dr/view`, new Blob([body], { type: "text/plain" }));
  else void fetch(`${API_URL}/dr/view`, { method: "POST", headers: { "Content-Type": "text/plain" }, body, keepalive: true }).catch(() => {});
}

export const fmtBytes = (b: number) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
